"use client";

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FieldValues, UseFormReturn } from "react-hook-form";

import { Form } from "@/components/Form";
import { Typography } from "@/components/Typography";
import { Button } from "@/components/Button";
import { GoogleAuthButton } from "@/components/GoogleAuthButton";

import { registerValidation } from "@/utils/validators/register";
import { ApiErrorMessageBuilder } from "@/utils/apiErrorHandler";

import { getSiteInfo } from "@/sites";

import { getCookie } from "@/services/CookiesService";

import { useToast } from "@/hooks/ui/useToast";
import { useRegister } from "@/hooks/auth/useRegister";

import { GTM_EVENT, GTM_EVENT_NAME } from "@/internals/constants/events";
import { NEXT_LOCALE } from "@/internals/constants/cookies";
import { NAMESPACES } from "@/internals/constants/namespaces";

import { RegisterFormProps } from "./types";
import { FormValidationMethods } from "@/components/Form/types";
import { QR_REFERENCE_COOKIE } from "@/containers/AuthContainer/types";
import { API_ERROR_FALLBACKS } from "@/internals/constants/errorCodes";
import { decodeJwt } from "@/services/JwtService/JwtService";

export default function Register({
  onSetFormType,
  onRegister,
  setIsLoading,
  isLoading,
  source,
  registerData,
}: RegisterFormProps) {
  const { toast } = useToast();
  const { t } = useTranslation(NAMESPACES.GENERIC_ERRORS);

  const { onHandleRegister, registerResponse, registerError } = useRegister();

  const [isGoogleLoginLoading, setIsGoogleLoginLoading] = useState(false);
  const [password, setPassword] = useState("");
  const [formObject, setFormObject] = useState<
    { values: FieldValues; methods: UseFormReturn } | undefined
  >(undefined);

  const schema = registerValidation(registerData, password);

  const {
    richtext: { Tag, content, className },
    button: { id, label },
  } = registerData.authModeSwitcher;

  const onRegisterSubmit = async (values: FieldValues, methods: UseFormReturn) => {
    setIsLoading(true);
    setFormObject({ values, methods });
    onHandleRegister({
      email: values.email,
      password: values.password,
      reference: getCookie(QR_REFERENCE_COOKIE),
      locale: getCookie(NEXT_LOCALE),
    });
  };

  const onGoogleAuthSuccess = (userData: any) => {
    const token = userData?.data?.auth?.token;

    const decoded = decodeJwt(token);
    window?.dataLayer?.push({
      event: GTM_EVENT_NAME.SIGN_UP_COMPLETED,
      source,
      email: decoded?.email || "unknown",
      "user-id": decoded?.id || "unknown",
    } as GTM_EVENT);
    onRegister();
  };

  useEffect(() => {
    if (!formObject) return;
    if (registerResponse?.status === 200 || registerResponse?.status === 201) {
      window?.dataLayer?.push({
        event: GTM_EVENT_NAME.SIGN_UP_COMPLETED,
        source,
        email: formObject.values?.email || "unknown",
        "user-id": registerResponse?.data?.userId || "unknown",
      } as GTM_EVENT);
      setIsLoading(true);
      onRegister(formObject.values, formObject.methods);
    }
  }, [registerResponse]);

  useEffect(() => {
    if (!formObject) return;

    if (registerError?.status === 400 || registerError?.status === 401) {
      formObject.methods.setError("email", {
        message: registerData.invalidAuthMessage,
      });
      formObject.methods.resetField("password", { defaultValue: "" });
    } else {
      const errorMessage = ApiErrorMessageBuilder.buildToastMessage(registerError, {
        fallbackMessage: API_ERROR_FALLBACKS.AUTH.REGISTER,
      });

      toast({ message: errorMessage, type: "error" });
    }
    setIsLoading(false);
  }, [registerError]);

  const checkPassword = (value: string, methods: FormValidationMethods) => {
    setPassword(value);
    methods.trigger("password");
  };

  const handleLoginClick = () => {
    onSetFormType("login");
  };

  const { roundedAuthButtons } = getSiteInfo(process.env.NEXT_PUBLIC_DOMAIN!);

  return (
    <div
      className="Register"
      data-testid="Register"
    >
      <Typography
        Tag="p"
        classname="Register__title"
        size="xl"
        spacing={5}
      >
        {registerData.title}
      </Typography>
      <GoogleAuthButton
        type="signup"
        label={t("google_login_button")}
        rounded={roundedAuthButtons}
        isLoading={setIsGoogleLoginLoading}
        onSuccess={onGoogleAuthSuccess}
        isFormLoading={isLoading}
        errors={{
          registerError: registerData.invalidAuthGoogleMessage,
          genericError: t("generic_error"),
        }}
        loadingLabel={t("google_login_button_loading")}
      />
      <Form
        fields={registerData.formFields}
        onSubmit={onRegisterSubmit}
        schema={schema}
        validationMode="onSubmit"
        isDisabled={isGoogleLoginLoading}
        isLoading={isLoading}
        onWatchFields={({ field, value, methods }) => {
          if (field === "password") checkPassword(value, methods);
        }}
        isAuth
      />

      <div className="Auth__switcher">
        <Typography
          Tag={Tag}
          spacing={2}
          classname={className}
        >
          {content}
        </Typography>
        {/* //TODO: components-lib: This button will be replace by a Link Component */}
        <Button
          ghost
          spacing={2}
          buttonId={id}
          label={label}
          disabled={isGoogleLoginLoading}
          dataQA="register-modal-login-button"
          onClick={handleLoginClick}
          className="switch-form-button"
        />
      </div>
      <div className="register-disclaimer">
        <span dangerouslySetInnerHTML={{ __html: registerData.disclaimer }}></span>
      </div>
    </div>
  );
}
